import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Instagram, Facebook, Linkedin } from "lucide-react";
import SEO from "@/components/SEO";
import { motion } from "framer-motion";
import BackgroundGradient from "@/components/landing/BackgroundGradient";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import ContactModal from "@/components/landing/ContactModal";
import { FounderCard, type Founder } from "@/components/landing/AboutSection";
import { SOCIAL_LINKS } from "@/lib/constants";

export default function AboutPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const { t } = useTranslation();

  const founders: Founder[] = [
    {
      role: t("dyna.about.role1"),
      name: t("dyna.about.name1"),
      initials: "PH",
      hue: 270,
      bio: t("dyna.about.bio1"),
      quote: t("dyna.about.quote1"),
      tags: [t("dyna.about.tag1a"), t("dyna.about.tag1b"), t("dyna.about.tag1c")],
      photo: "/team/pinch.webp",
      linkedin: "",
      instagram: "",
    },
    {
      role: t("dyna.about.role2"),
      name: t("dyna.about.name2"),
      initials: "ER",
      hue: 255,
      bio: t("dyna.about.bio2"),
      quote: t("dyna.about.quote2"),
      tags: [t("dyna.about.tag2a"), t("dyna.about.tag2b"), t("dyna.about.tag2c")],
      photo: "/team/ezra.webp",
      linkedin: "https://www.linkedin.com/in/ezra-rothchild-624b9a203/",
      instagram: "https://www.instagram.com/ezra_rothchild/",
    },
  ];

  const agencySocials = [
    { icon: Instagram, href: SOCIAL_LINKS.instagram, label: "Instagram" },
    { icon: Facebook,  href: SOCIAL_LINKS.facebook,  label: "Facebook"  },
    { icon: Linkedin,  href: SOCIAL_LINKS.linkedin,  label: "LinkedIn"  },
  ].filter(s => s.href);

  return (
    <>
      <SEO title="About Us, Pinch & Ezra, Dyna Agency" description="Dyna Agency is a Jerusalem-based web design and SEO/GEO studio founded by Pinch and Ezra. 15+ sites shipped. Serving Israel and the US." path="/about" />
      <BackgroundGradient />
      <div style={{ minHeight: "100vh", color: "var(--dyna-fg)", position: "relative", zIndex: 1 }}>
        <Navbar onCTA={() => setModalOpen(true)} activePage="about" />
        <main style={{ paddingTop: 120 }}>
          {/* Page hero */}
          <section style={{ padding: "var(--s-xl) var(--site-px) var(--s-lg)", maxWidth: "var(--site-max)", margin: "0 auto" }}>
            <span className="eyebrow" style={{ display: "block", marginBottom: 8 }}>{t("pages.about.eyebrow")}</span>
            <h1 style={{
              fontFamily: "Coolvetica, system-ui, sans-serif",
              fontSize: "clamp(48px, 7vw, 80px)", fontWeight: 700,
              letterSpacing: "0.05em", textTransform: "uppercase",
              margin: "0 0 20px", lineHeight: 1.05,
            }}>
              {t("pages.about.title")}
            </h1>
            <p style={{
              fontFamily: "Geist, system-ui, sans-serif", fontWeight: 300,
              fontSize: 18, lineHeight: 1.7, color: "var(--dyna-fg-muted)", maxWidth: 560,
            }}>
              {t("pages.about.subtitle")}
            </p>
            {agencySocials.length > 0 && (
              <div style={{ display: "flex", gap: 18, marginTop: 24 }}>
                {agencySocials.map(s => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    style={{
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      color: "var(--dyna-fg-muted)",
                      transition: "color 200ms ease",
                      textDecoration: "none",
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = "var(--quartz-200)"; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = "var(--dyna-fg-muted)"; }}
                  >
                    <s.icon size={20} />
                  </a>
                ))}
              </div>
            )}
          </section>

          {/* Better Together — full-width card */}
          <section style={{ padding: "0 var(--site-px) var(--s-lg)", maxWidth: "var(--site-max)", margin: "0 auto" }}>
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="dyna-about__better" style={{
                borderRadius: 24,
                overflow: "hidden",
                border: "1px solid var(--dyna-border)",
                background: "radial-gradient(ellipse at 50% 0%, var(--color-soft) 0%, transparent 65%), linear-gradient(180deg, var(--dyna-bg-raised), var(--dyna-bg-elevated))",
                boxShadow: "var(--glow-nova)",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
              }}>
                {/* Combined photo placeholder — photos take equal width and a
                    portrait-golden (1 : 1.618) aspect, so they fill the left
                    column with consistent proportions and drive the card height. */}
                <div style={{
                  position: "relative",
                  background: "linear-gradient(135deg, var(--dyna-bg) 0%, var(--dyna-bg-raised) 50%, var(--dyna-bg-elevated) 100%)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  padding: "clamp(28px, 4vw, 56px)",
                  overflow: "hidden",
                }}>
                  <div style={{ display: "flex", gap: 16, width: "100%" }}>
                    {founders.map(f => (
                      <div key={f.name} style={{
                        flex: "1 1 0",
                        aspectRatio: "1 / 1.4",
                        borderRadius: 18,
                        background: `linear-gradient(145deg, hsl(${f.hue},48%,22%) 0%, hsl(${f.hue - 15},42%,12%) 100%)`,
                        border: "1px solid var(--dyna-border)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: `0 12px 40px -8px hsla(${f.hue},45%,40%,0.3)`,
                        overflow: "hidden",
                      }}>
                        {f.photo ? (
                          <img src={f.photo} alt={f.name} style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }} />
                        ) : (
                          <span style={{
                            fontFamily: "Coolvetica, system-ui, sans-serif",
                            fontSize: "clamp(36px, 5vw, 56px)", fontWeight: 700,
                            color: `hsla(${f.hue},60%,80%,0.4)`,
                            letterSpacing: "0.06em",
                          }}>
                            {f.initials}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  <div style={{
                    position: "absolute", inset: 0,
                    background: "linear-gradient(to right, transparent 60%, rgba(10,8,15,0.6))",
                    pointerEvents: "none",
                  }} />
                </div>

                {/* Copy */}
                <div style={{ padding: "clamp(32px, 5vw, 56px)", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <span style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
                    color: "var(--lavender-500)", marginBottom: 16, display: "block",
                  }}>
                    {t("dyna.about.better_eyebrow")}
                  </span>
                  <h3 style={{
                    fontFamily: "Coolvetica, system-ui, sans-serif",
                    fontSize: "clamp(28px, 3vw, 40px)",
                    fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
                    color: "var(--dyna-fg)", margin: "0 0 20px", lineHeight: 1.05,
                  }}>
                    {t("dyna.about.better_heading")}<br />
                    <span style={{
                      fontFamily: "'Instrument Serif', serif",
                      fontStyle: "italic", fontWeight: 400, textTransform: "lowercase",
                      color: "var(--lavender-300)",
                    }}>
                      {t("dyna.about.better_heading_em")}
                    </span>
                  </h3>
                  <p style={{
                    fontSize: 15, lineHeight: 1.7, color: "var(--dyna-fg-muted)",
                    fontFamily: "Geist, system-ui, sans-serif", fontWeight: 300,
                    margin: "0 0 28px", maxWidth: 400,
                  }}>
                    {t("dyna.about.better_body")}
                  </p>
                  <a
                    href="/#contact"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 8,
                      fontSize: 13, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase",
                      fontFamily: "Geist, system-ui, sans-serif",
                      color: "var(--lavender-300)", textDecoration: "none",
                      border: "1px solid var(--dyna-border-strong)",
                      borderRadius: 999, padding: "10px 22px",
                      transition: "all 200ms", width: "fit-content",
                    }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLElement).style.background = "var(--lavender-600)";
                      (e.currentTarget as HTMLElement).style.color = "#fff";
                      (e.currentTarget as HTMLElement).style.borderColor = "var(--lavender-600)";
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLElement).style.background = "transparent";
                      (e.currentTarget as HTMLElement).style.color = "var(--lavender-300)";
                      (e.currentTarget as HTMLElement).style.borderColor = "var(--dyna-border-strong)";
                    }}
                  >
                    {t("dyna.about.better_cta")} <span style={{ marginLeft: 2 }}>→</span>
                  </a>
                </div>
              </div>
            </motion.div>
          </section>

          {/* Individual founder cards — side by side */}
          <section style={{ padding: "0 var(--site-px) var(--s-lg)", maxWidth: "var(--site-max)", margin: "0 auto" }}>
            <div className="dyna-about__people">
              {founders.map((f) => (
                <FounderCard key={f.name} founder={f} />
              ))}
            </div>
          </section>
        </main>
        <Footer />
      </div>
      <ContactModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
}
